import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { SquareTerminal, ChevronDown, ChevronUp, Download, RotateCcw, FileText, FileSpreadsheet, FileImage, Eye, AlertCircle } from 'lucide-react';
import AnalysisFilePreview, { previewKind } from './AnalysisFilePreview';
import { toast } from 'react-hot-toast';
import { authHeader } from '../../utils/authHeaders';
import { downloadAuthenticatedFile } from '../../utils/downloadFile';
import { BASE_DATA_ANALYSIS_URL, BASE_WORDURL } from '../constants';
import { completeAnalysisJob, failAnalysisJob } from '../../slice/chatSlice';
import { fetchGeneratedDocuments } from '../../slice/apiSlice';
export const ANALYSIS_BLUE = '#2563EB';
export const ANALYSIS_BLUE_SOFT = '#EFF6FF';
export const ANALYSIS_BLUE_BORDER = '#BFDBFE';

const useAccent = () => ({ accent: ANALYSIS_BLUE, soft: ANALYSIS_BLUE_SOFT, border: ANALYSIS_BLUE_BORDER });
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 15000;

const STEPS = [
    { key: 'preparing', label: 'dataAnalysis.stepPreparing' },
    { key: 'inspecting', label: 'dataAnalysis.stepInspecting' },
    { key: 'calculating', label: 'dataAnalysis.stepCalculating' },
    { key: 'results', label: 'dataAnalysis.stepResults' },
];

const scopeSubtitleKey = (scope, files, isPending) => {
    if (!files?.length && !isPending) return 'dataAnalysis.noFilesForRequest';
    return scope === 'selected' ? 'dataAnalysis.selectedForRequest' : 'dataAnalysis.sessionForRequest';
};

const fileIconFor = (filename) => {
    const ext = (filename || '').split('.').pop().toLowerCase();
    if (['xlsx', 'xls', 'csv'].includes(ext)) return FileSpreadsheet;
    if (['png', 'jpg', 'jpeg'].includes(ext)) return FileImage;
    return FileText;
};

function StatusPill({ status, t, accent }) {
    const styles = {
        running: { bg: '#F3F4F6', text: '#374151', dot: accent, label: 'dataAnalysis.statusAnalyzing' },
        done: { bg: '#ECFDF5', text: '#047857', dot: '#10B981', label: 'dataAnalysis.statusReady' },
        error: { bg: '#FEF2F2', text: '#B91C1C', dot: '#EF4444', label: 'dataAnalysis.statusFailed' },
    }[status] || null;
    if (!styles) return null;
    return (
        <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-medium flex-shrink-0"
            style={{ backgroundColor: styles.bg, color: styles.text }}
        >
            <span className={`w-1.5 h-1.5 rounded-full ${status === 'running' ? 'animate-pulse' : ''}`} style={{ backgroundColor: styles.dot }} />
            {t(styles.label)}
        </span>
    );
}

export function AnalysisJobCard({ job, historyId, userId, t, onRetry }) {
    const dispatch = useDispatch();
    const { accent, soft, border } = useAccent();
    const [step, setStep] = useState(job.step || 'preparing');
    const [progress, setProgress] = useState(job.progress || 0);
    const status = job.status || 'running';
    const jobId = job.job_id;

    useEffect(() => {
        if (!jobId || status !== 'running') return undefined;
        let cancelled = false;
        let timer;
        let controller;
        const poll = async () => {
            controller = new AbortController();
            const abortTimer = setTimeout(() => controller.abort(), POLL_TIMEOUT_MS);
            try {
                const res = await fetch(`${BASE_DATA_ANALYSIS_URL}/status/${encodeURIComponent(jobId)}`, { headers: { ...authHeader() }, signal: controller.signal });
                if (cancelled) return;
                if (res.status === 404) {
                    dispatch(failAnalysisJob({ jobId }));
                    return;
                }
                const data = await res.json();
                if (cancelled) return;
                setStep(data.step || 'preparing');
                setProgress(data.progress || 0);
                if (data.status === 'running') {
                    timer = setTimeout(poll, POLL_INTERVAL_MS);
                } else if (data.status === 'done' && data.result) {
                    dispatch(completeAnalysisJob({ jobId, result: data.result }));
                    if (data.result.analysis?.generated?.length) {
                        dispatch(fetchGeneratedDocuments({ userId, historyId }));
                    }
                } else {
                    dispatch(failAnalysisJob({ jobId, errorCode: data.error_code, skipped: data.skipped }));
                }
            } catch (e) {
                if (!cancelled) timer = setTimeout(poll, POLL_INTERVAL_MS);
            } finally {
                clearTimeout(abortTimer);
            }
        };
        timer = setTimeout(poll, 1000);
        return () => { cancelled = true; clearTimeout(timer); controller?.abort(); };
    }, [jobId, status, dispatch, userId, historyId]);

    const isDone = status === 'done';
    const isError = status === 'error';
    const currentIndex = isDone ? STEPS.length : Math.max(0, STEPS.findIndex((s) => s.key === step));
    const currentStepLabel = STEPS[Math.min(currentIndex, STEPS.length - 1)].label;
    const shownProgress = isDone ? 100 : Math.max(4, Math.min(progress, 99));
    const files = job.files || [];

    const title = isDone
        ? t('dataAnalysis.completedTitle')
        : isError
            ? t('dataAnalysis.failedTitle')
            : t('dataAnalysis.workingTitle');

    return (
        <div className="mb-4">
            <p className="text-[14px] font-semibold text-[#111827] mb-3">{title}</p>
            <div className="border border-[#E5E7EB] rounded-xl p-4 max-w-[720px]">
                <div className="flex items-start gap-3">
                    <div
                        className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg"
                        style={{ backgroundColor: soft, color: accent }}
                    >
                        <SquareTerminal size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-[14px] font-semibold text-[#111827] truncate" title={files.join(', ')}>
                            {files.length ? files.join(', ') : t('dataAnalysis.modeName')}
                        </p>
                        <p className="text-[12px] text-[#6B7280]">{t(scopeSubtitleKey(job.scope, files, !jobId && status === 'running'))}</p>
                    </div>
                    <StatusPill status={status} t={t} accent={accent} />
                </div>

                {!isError && (
                    <div className="mt-4">
                        <div className="flex items-center justify-between mb-1.5">
                            <span className="text-[13px] text-[#374151]">
                                {isDone ? t('dataAnalysis.progressCompleted') : step === 'retrying' ? t('dataAnalysis.stepRetrying') : t(currentStepLabel)}
                            </span>
                            <span className="text-[12px] text-[#9CA3AF]">{shownProgress}%</span>
                        </div>
                        <div
                            className="h-1.5 rounded-full bg-[#F3F4F6] overflow-hidden"
                            role="progressbar"
                            aria-valuenow={shownProgress}
                            aria-valuemin={0}
                            aria-valuemax={100}
                        >
                            <div
                                className="h-full rounded-full transition-all duration-500"
                                style={{ width: `${shownProgress}%`, backgroundColor: accent }}
                            />
                        </div>
                        <ul className="mt-4 pt-3 border-t border-[#F3F4F6] space-y-1.5">
                            {STEPS.map((s, index) => {
                                const isCurrent = !isDone && index === currentIndex;
                                const isComplete = isDone || index < currentIndex;
                                return (
                                    <li key={s.key} className="flex items-center gap-2 text-[12.5px]" data-step={s.key} data-state={isCurrent ? 'current' : isComplete ? 'complete' : 'pending'}>
                                        <span
                                            className={`w-2 h-2 rounded-full flex-shrink-0 ${isCurrent ? 'animate-pulse' : ''}`}
                                            style={{ backgroundColor: isCurrent ? accent : isComplete ? '#374151' : '#D1D5DB' }}
                                        />
                                        <span style={{ color: isCurrent ? accent : isComplete ? '#374151' : '#9CA3AF' }}>
                                            {t(s.label)}
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}

                {isDone && (
                    <p className="mt-3 pt-3 border-t border-[#F3F4F6] text-[12px] text-[#6B7280]">{t('dataAnalysis.completedHelper')}</p>
                )}

                {isError && (
                    <div className="mt-4 flex items-center justify-between gap-3">
                        <p className="text-[13px] text-[#6B7280]">{t({ content_filter: 'dataAnalysis.contentFilterHelper', files_unavailable: 'dataAnalysis.filesUnavailableHelper', max_output_tokens: 'dataAnalysis.tokenLimitHelper' }[job.errorCode] || 'dataAnalysis.failedHelper')}</p>
                        {onRetry && job.payload && job.errorCode !== 'max_output_tokens' && (
                            <button
                                type="button"
                                onClick={onRetry}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[13px] font-medium transition-colors hover:bg-[var(--analysis-soft)]"
                                style={{ borderColor: border, color: accent, '--analysis-soft': soft }}
                            >
                                <RotateCcw size={13} />
                                {t('dataAnalysis.retry')}
                            </button>
                        )}
                    </div>
                )}
                {isError && job.errorCode === 'files_unavailable' && job.skipped?.length > 0 && (
                    <ul className="mt-2 space-y-1" data-testid="analysis-failed-files">
                        {job.skipped.map((entry, index) => (
                            <li key={`${entry.name}-${index}`} className="flex items-center gap-1.5 text-[12.5px] text-[#6B7280] min-w-0">
                                <AlertCircle size={12} className="flex-shrink-0 text-[#D97706]" />
                                <span className="truncate min-w-0" title={entry.name}>{entry.name}</span>
                                <span className="flex-shrink-0 text-[#9CA3AF]">– {t(SKIP_REASON_KEYS[entry.reason] || 'dataAnalysis.skipReasonUnreadable')}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

const SKIP_REASON_KEYS = {
    missing: 'dataAnalysis.skipReasonMissing',
    unreadable: 'dataAnalysis.skipReasonUnreadable',
    too_large: 'dataAnalysis.skipReasonTooLarge',
    standard_file: 'dataAnalysis.skipReasonStandardFile',
};

export function AnalysisResultPanel({ analysis, t }) {
    const [expanded, setExpanded] = useState(false);
    const [previewDoc, setPreviewDoc] = useState(null);
    const { accent } = useAccent();
    const files = analysis?.files || [];
    const generated = analysis?.generated || [];
    const skipped = analysis?.skipped || [];
    const headerKey = !files.length
        ? 'dataAnalysis.basedOnNone'
        : analysis?.scope === 'selected'
            ? 'dataAnalysis.basedOnSelected'
            : 'dataAnalysis.basedOnSession';

    const handleDownload = (doc) => {
        downloadAuthenticatedFile(`${BASE_WORDURL}/download/${encodeURIComponent(doc.id)}`, doc.filename)
            .catch(() => toast.error(t('dataAnalysis.downloadError')));
    };

    return (
        <div className="mt-4 border border-[#E5E7EB] rounded-lg overflow-hidden">
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                aria-expanded={expanded}
                className="w-full flex items-center justify-between px-4 py-3 bg-[#F9FAFB] hover:bg-[#F3F4F6] transition-colors"
            >
                <span className="flex items-center gap-2 text-[14px] font-medium text-[#374151]">
                    <SquareTerminal size={14} style={{ color: accent }} />
                    {t(headerKey)}
                </span>
                {expanded ? <ChevronUp size={18} className="text-[#6B7280]" /> : <ChevronDown size={18} className="text-[#6B7280]" />}
            </button>
            {expanded && (
                <div className="px-4 py-3 space-y-3">
                    {files.length > 0 && (
                        <div>
                            <p className="text-[12px] font-medium text-[#6B7280] mb-1.5">{t('dataAnalysis.filesUsedLabel')}</p>
                            <div className="flex flex-wrap gap-1.5">
                                {files.map((name, index) => {
                                    const Icon = fileIconFor(name);
                                    return (
                                        <span
                                            key={`${name}-${index}`}
                                            className="inline-flex items-center gap-1.5 pl-2.5 pr-3 py-1 rounded-full bg-gray-50 border border-gray-100 text-[12.5px] text-gray-600 max-w-[260px]"
                                        >
                                            <Icon size={12} className="flex-shrink-0" />
                                            <span className="truncate min-w-0" title={name}>{name}</span>
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    {skipped.length > 0 && (
                        <div data-testid="analysis-skipped">
                            <p className="text-[12px] font-medium text-[#6B7280] mb-1.5">{t('dataAnalysis.skippedLabel')}</p>
                            <ul className="space-y-1">
                                {skipped.map((entry, index) => (
                                    <li key={`${entry.name}-${index}`} className="flex items-center gap-1.5 text-[12.5px] text-[#6B7280] min-w-0">
                                        <AlertCircle size={12} className="flex-shrink-0 text-[#D97706]" />
                                        <span className="truncate min-w-0" title={entry.name}>{entry.name}</span>
                                        <span className="flex-shrink-0 text-[#9CA3AF]">– {t(SKIP_REASON_KEYS[entry.reason] || 'dataAnalysis.skipReasonUnreadable')}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {generated.length > 0 && (
                        <div>
                            <p className="text-[12px] font-medium text-[#6B7280] mb-1.5">{t('dataAnalysis.generatedFilesLabel')}</p>
                            <div className="flex flex-wrap gap-2">
                                {generated.map((doc) => {
                                    const Icon = fileIconFor(doc.filename);
                                    return (
                                        <div
                                            key={doc.id}
                                            className="inline-flex items-center gap-2 pl-2.5 pr-1 py-1 rounded-lg border border-[#E5E7EB] bg-white text-[13px] text-[#111827] max-w-[320px]"
                                        >
                                            <Icon size={14} className="flex-shrink-0" style={{ color: accent }} />
                                            <span className="truncate min-w-0" title={doc.filename}>{doc.filename}</span>
                                            {previewKind(doc.filename) && (
                                                <button
                                                    type="button"
                                                    onClick={() => setPreviewDoc(doc)}
                                                    title={t('dataAnalysis.preview')}
                                                    aria-label={t('dataAnalysis.preview')}
                                                    className="w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-md text-[#6B7280] hover:bg-[#F3F4F6]"
                                                >
                                                    <Eye size={14} />
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => handleDownload(doc)}
                                                title={t('dataAnalysis.download')}
                                                aria-label={t('dataAnalysis.download')}
                                                className="w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-md text-[#6B7280] hover:bg-[#F3F4F6]"
                                            >
                                                <Download size={14} />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}
            {previewDoc && <AnalysisFilePreview doc={previewDoc} t={t} onClose={() => setPreviewDoc(null)} />}
        </div>
    );
}
